import { useState, useEffect } from 'react';
import { StaffSidebar } from '../../components/StaffSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, User, Mail, Phone, Hash } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffCustomers() {
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/staff/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(search.toLowerCase()) ||
    customer.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={`min-h-screen bg-warm-sand ${isRTL ? 'rtl' : 'ltr'}`}>
      <StaffSidebar />
      
      <main className={`pt-16 lg:pt-0 ${isRTL ? 'lg:mr-64' : 'lg:ml-64'}`}>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            <div>
              <h1 className="font-heading text-3xl font-bold text-deep-charcoal">
                {t('customers')}
              </h1>
              <p className="font-body text-deep-charcoal/60 mt-1">
                {t('viewCustomerHistory')}
              </p>
            </div>
            
            <div className="relative w-full sm:w-64">
              <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-deep-charcoal/40 ${isRTL ? 'right-3' : 'left-3'}`} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('searchCustomers')}
                className={`rounded-xl bg-white border-deep-charcoal/20 ${isRTL ? 'pr-10' : 'pl-10'}`}
                data-testid="customer-search"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <Card className="bg-white rounded-xl border-0 shadow-soft">
              <CardContent className="p-12 text-center">
                <User className="w-16 h-16 text-deep-charcoal/20 mx-auto mb-4" />
                <p className="font-body text-deep-charcoal/60">
                  {search ? t('noCustomersFound') : t('noCustomersYet')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredCustomers.map((customer, index) => (
                <Card 
                  key={customer.user_id}
                  className="bg-white rounded-xl border-0 shadow-soft animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`customer-card-${customer.user_id}`}
                >
                  <CardContent className="p-6">
                    <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-heading text-lg font-bold text-terracotta">
                          {customer.name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg font-semibold text-deep-charcoal truncate">
                          {customer.name}
                        </h3>
                        <div className="space-y-2 mt-3">
                          <div className={`flex items-center text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Mail className={`w-4 h-4 text-terracotta flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-body text-sm truncate">{customer.email}</span>
                          </div>
                          {customer.phone && (
                            <div className={`flex items-center text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Phone className={`w-4 h-4 text-terracotta flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                              <span className="font-body text-sm">{customer.phone}</span>
                            </div>
                          )}
                          <div className={`flex items-center text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Hash className={`w-4 h-4 text-sage flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-body text-sm">{customer.booking_count} {t('bookings').toLowerCase()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
